/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import PaymentsService from '../../../Services/PaymentsService';
import { useAccountsList } from '../../../Hooks/useAccountsCache';
import Error from '../../../Components/Error';

// Local YYYY-MM-DD for today. We deliberately use the local zone here
// (not UTC) so the date input pre-fills with what the admin sees on the
// wall calendar; on submit we convert to a full ISO string.
function todayYmd() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function RecordWirePaymentModal({
  show, onHide, booking, onPaymentRecorded,
}) {
  const defaultAmount = booking?.total_price ?? booking?.price ?? '';
  const defaultCurrency = booking?.currency || 'EUR';

  const [amount, setAmount] = useState(defaultAmount);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [occurredAt, setOccurredAt] = useState(todayYmd());
  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');
  const [accountId, setAccountId] = useState('');
  const [validationError, setValidationError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pull the accounts list from the shared cache. We only let the
  // admin pick from ACTIVE accounts; inactive accounts are hidden.
  // The picker defaults to the first active bank account (the most
  // likely landing zone for a wire), falling back to the first active
  // account of any kind if no bank exists.
  const { accounts: allAccounts } = useAccountsList();
  const activeAccounts = useMemo(
    () => allAccounts.filter((a) => a.is_active),
    [allAccounts],
  );
  const defaultAccountId = useMemo(() => {
    const bank = activeAccounts.find((a) => a.kind === 'bank');
    if (bank) return bank._id;
    return activeAccounts[0]?._id || '';
  }, [activeAccounts]);

  // Re-seed defaults whenever the modal is (re)opened or the booking
  // changes. Without this, closing and re-opening for a different
  // booking would keep stale form values.
  useEffect(() => {
    if (show) {
      setAmount(booking?.total_price ?? booking?.price ?? '');
      setCurrency(booking?.currency || 'EUR');
      setOccurredAt(todayYmd());
      setBankReference('');
      setNotes('');
      setValidationError('');
      setError('');
    }
  }, [show, booking?._id, booking?.total_price, booking?.price, booking?.currency]);

  // Default-pick the first active bank account once the cache resolves.
  // We only do this if the admin hasn't already picked something — so a
  // stale default doesn't stomp an intentional selection if the modal
  // re-renders.
  useEffect(() => {
    if (!show) return;
    if (accountId) return;
    if (defaultAccountId) setAccountId(defaultAccountId);
  }, [show, defaultAccountId, accountId]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setValidationError('');

    const grossNum = Number(amount);
    if (!amount || Number.isNaN(grossNum) || grossNum <= 0) {
      setValidationError('Amount must be greater than 0');
      return;
    }
    if (!currency) {
      setValidationError('Currency is required');
      return;
    }
    if (!occurredAt) {
      setValidationError('Received date is required');
      return;
    }
    if (!accountId) {
      setValidationError('Account is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        direction: 'in',
        account_id: accountId,
        gross_amount: grossNum,
        currency,
        occurred_at: new Date(occurredAt).toISOString(),
        counterparty: { kind: 'booking', id: booking._id },
        external_refs: { bank_reference: bankReference || undefined },
        notes: notes || undefined,
      };
      const payment = await PaymentsService.recordPayment(payload);
      if (onPaymentRecorded) onPaymentRecorded(payment);
      onHide();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-university me-2" />
          Record Wire Payment
          {booking?.booking_reference && (
            <small className="text-muted ms-2" style={{ fontSize: '0.7em' }}>
              {booking.booking_reference}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />
        {validationError && (
          <div className="alert alert-warning py-2 mb-3 small" role="alert">
            {validationError}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="rwp_amount">
            <Form.Label>
              Amount
              {' '}
              *
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <Form.Text className="text-muted">
              In major units (e.g. 99.99 EUR, not cents).
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="rwp_currency">
            <Form.Label>
              Currency
              {' '}
              *
            </Form.Label>
            <Form.Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="EUR">EUR</option>
              <option value="HUF">HUF</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="rwp_occurred_at">
            <Form.Label>
              Received on
              {' '}
              *
            </Form.Label>
            <Form.Control
              type="date"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="rwp_account">
            <Form.Label>
              Account
              {' '}
              *
            </Form.Label>
            <Form.Select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">Select account…</option>
              {activeAccounts.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Which bank account did the wire land in?
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="rwp_bank_reference">
            <Form.Label>Bank reference</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. statement line ID"
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="rwp_notes">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || !accountId}
        >
          {loading ? 'Recording…' : 'Record Payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RecordWirePaymentModal;
